import { useState, useEffect, useRef } from 'react'
import { erpApi } from '../../api/erp'
import BuscadorArticulo from '../../components/BuscadorArticulo'
import MoneyInput from '../../components/MoneyInput'
import Boton from '../../components/Boton'
import { fmtImporte, totalRenglones, st } from './_helpers'
import { colors, text, space } from '../../design/tokens'

// Componentes compartidos de las pantallas EMP 900. Helpers puros, hooks y
// estilos viven en _helpers.js (fast refresh exige archivos solo-componentes).

// ---------------------------------------------------------------- depósitos

export function SelectDeposito({ value, onChange, depositos, style = {} }) {
  // Un solo depósito → se elige solo; con varios el operador decide.
  useEffect(() => {
    if (!value && depositos.length === 1) onChange(depositos[0].codigo)
  }, [depositos, value, onChange])
  return (
    <select style={{ ...st.input, ...style }} value={value} onChange={e => onChange(e.target.value)}>
      <option value="">Depósito…</option>
      {depositos.map(d => <option key={d.codigo} value={d.codigo}>{d.codigo} · {d.nombre}</option>)}
    </select>
  )
}

// ---------------------------------------------------------------- terceros

/**
 * Buscador contra el maestro del ERP (globales de emp_0 + propios de la 900,
 * ya filtrados allá). Avisa bloqueo/límite ANTES de facturar pero no impide.
 */
export function BuscadorTercero({ value, onChange, rol, onNuevo, placeholder }) {
  const [q, setQ] = useState('')
  const [resultados, setResultados] = useState([])
  const [abierto, setAbierto] = useState(false)
  const timer = useRef(null)
  const ref = useRef(null)

  useEffect(() => {
    const handler = (e) => { if (!ref.current?.contains(e.target)) setAbierto(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const buscar = (texto) => {
    setQ(texto)
    clearTimeout(timer.current)
    if (texto.trim().length < 2) { setResultados([]); return }
    timer.current = setTimeout(async () => {
      try {
        const { data } = await erpApi.terceros({ q: texto.trim(), rol })
        setResultados(data.terceros || [])
        setAbierto(true)
      } catch { setResultados([]) }
    }, 300)
  }

  if (value) {
    return (
      <div style={st.chipTercero}>
        <span style={st.chipCodigo}>{value.codigo || 's/cód'}</span>
        <span style={st.chipNombre}>{value.razon_social}</span>
        {value.documento && <span style={st.chipDoc}>{value.documento}</span>}
        {value.bloqueado && <span style={st.tagWarn} title={value.motivo_bloqueo || ''}>bloqueado</span>}
        <button type="button" style={st.chipQuitar} onClick={() => { onChange(null); setQ('') }} aria-label="Quitar">×</button>
      </div>
    )
  }

  return (
    <div ref={ref} style={{ position: 'relative', display: 'flex', gap: space.sm }}>
      <input
        style={{ ...st.input, flex: 1 }}
        value={q}
        onChange={e => buscar(e.target.value)}
        onFocus={() => resultados.length && setAbierto(true)}
        placeholder={placeholder || (rol === 'proveedor' ? 'Buscar proveedor por nombre, código o CUIT…' : 'Buscar cliente por nombre, código o CUIT…')}
      />
      {onNuevo && <Boton variante="secondary" onClick={onNuevo}>+ Nuevo</Boton>}
      {abierto && (
        <div style={st.dropdown}>
          {resultados.length === 0 && <div style={st.ddVacio}>Sin resultados en el ERP.</div>}
          {resultados.map(t => (
            <div key={t.id} style={st.ddItem} onMouseDown={() => { onChange(t); setAbierto(false); setQ('') }}>
              <div style={st.ddFila}>
                <span style={st.ddCodigo}>{t.codigo || '—'}</span>
                <span style={st.ddNombre}>{t.razon_social}</span>
                {t.bloqueado && <span style={st.tagWarn}>bloqueado</span>}
              </div>
              <div style={st.ddMeta}>
                {t.documento ? <span style={st.ddDoc}>{t.documento}</span> : 'sin documento'}
                {t.limite_credito != null && Number(t.limite_credito) > 0 && ` · límite ${fmtImporte(t.limite_credito)}`}
                {t.es_proveedor && !t.es_cliente && ' · proveedor'}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- renglones

/**
 * Renglones a VALOR FINAL: sku · cantidad · un solo importe (precio o costo).
 * El artículo se busca en el maestro de LGR (mismo maestro que el ERP: el
 * `codigo_alfa` de LGR ES el `sku` del ERP). Con `permitirLibre`, renglones
 * sin sku (factura de proveedor con descripción libre).
 */
export function RenglonesEditor({ renglones, onChange, campoImporte = 'precio', labelImporte = 'Precio final', permitirLibre = false, sugerirPrecio = false }) {
  const [lista, setLista] = useState(null) // nombre de la lista que sugiere (LISTALM), cuando aplica

  // Con `sugerirPrecio` (ventas de la 900) el precio se trae de la lista de
  // LGR ANTES de agregar el renglón, tal cual está en la lista. Si el
  // artículo no tiene precio ahí, el campo queda vacío y lo dice — no se
  // inventa. El operador siempre puede cambiarlo.
  const agregar = async (a) => {
    let precio = ''
    let sinLista = false
    if (sugerirPrecio && campoImporte === 'precio') {
      try {
        const { data } = await erpApi.precios([a.codigo_alfa])
        setLista(data.lista)
        precio = data.precios?.[a.codigo_alfa] ?? ''
        sinLista = precio === ''
      } catch { sinLista = true }
    }
    onChange([...renglones, { sku: a.codigo_alfa, descripcion: a.descripcion, cantidad: '', [campoImporte]: precio, sin_lista: sinLista }])
  }
  const agregarLibre = () => onChange([...renglones, { sku: null, descripcion: '', cantidad: '1', [campoImporte]: '' }])
  const set = (i, k, v) => onChange(renglones.map((r, j) => j === i ? { ...r, [k]: v } : r))
  const quitar = (i) => onChange(renglones.filter((_, j) => j !== i))
  const total = totalRenglones(renglones, campoImporte)

  return (
    <div>
      <div style={{ display: 'flex', gap: space.sm, alignItems: 'center' }}>
        <div style={{ flex: 1 }}><BuscadorArticulo onSelect={agregar} /></div>
        {permitirLibre && <Boton variante="ghost" onClick={agregarLibre}>+ Renglón libre</Boton>}
      </div>
      {renglones.length > 0 && (
        <table style={{ ...st.table, marginTop: space.md }}>
          <thead>
            <tr>
              <th style={st.th}>SKU</th>
              <th style={st.th}>Descripción</th>
              <th style={{ ...st.th, width: 90 }}>Cantidad</th>
              <th style={{ ...st.th, width: 140 }}>{labelImporte}{sugerirPrecio && lista ? ` (${lista})` : ''}</th>
              <th style={{ ...st.th, textAlign: 'right', width: 130 }}>Total</th>
              <th style={{ ...st.th, width: 32 }}></th>
            </tr>
          </thead>
          <tbody>
            {renglones.map((r, i) => (
              <tr key={i} style={st.tr}>
                <td style={st.tdMono}>{r.sku || <span style={{ color: colors.textMeta }}>libre</span>}</td>
                <td style={st.td}>
                  {r.sku
                    ? r.descripcion
                    : <input style={st.inputCelda} value={r.descripcion} placeholder="Descripción" onChange={e => set(i, 'descripcion', e.target.value)} />}
                </td>
                <td style={st.td}>
                  <input style={{ ...st.inputCelda, textAlign: 'right' }} inputMode="decimal" value={r.cantidad} placeholder=""
                    onChange={e => set(i, 'cantidad', e.target.value.replace(/[^\d.]/g, ''))} />
                </td>
                <td style={st.td}>
                  <MoneyInput style={{ ...st.inputCelda, textAlign: 'right' }} value={r[campoImporte]} onChange={v => set(i, campoImporte, v)}
                    placeholder={r.sin_lista && r[campoImporte] === '' ? `sin ${lista || 'lista'}` : ''} title={r.sin_lista ? `Este artículo no tiene precio en ${lista || 'la lista'}: cargalo a mano.` : ''} />
                </td>
                <td style={st.tdImporte}>{fmtImporte((parseFloat(r.cantidad) || 0) * (parseFloat(r[campoImporte]) || 0))}</td>
                <td style={st.td}><button type="button" style={st.quitar} onClick={() => quitar(i)} aria-label="Quitar renglón">×</button></td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={4} style={{ ...st.td, textAlign: 'right', color: colors.textSec, fontSize: text.sec }}>Total</td>
              <td style={{ ...st.tdImporte, fontSize: text.title }}>{fmtImporte(total)}</td>
              <td />
            </tr>
          </tfoot>
        </table>
      )}
    </div>
  )
}

// ---------------------------------------------------------------- resultado / avisos

/** Los [AVISO] del ERP (cliente bloqueado, límite excedido) se MUESTRAN; la operación ya está hecha. */
export function AvisoErp({ observaciones }) {
  if (!observaciones || !String(observaciones).includes('[AVISO]')) return null
  const avisos = String(observaciones).split(/(?=\[AVISO\])/).filter(s => s.startsWith('[AVISO]'))
  return (
    <div style={st.aviso}>
      {avisos.map((a, i) => <div key={i}>⚠ {a.replace('[AVISO]', '').trim()}</div>)}
    </div>
  )
}

export function CajaError({ children }) {
  if (!children) return null
  return <div style={st.error}>{children}</div>
}

export function CajaOk({ titulo, children }) {
  return (
    <div style={st.ok}>
      <div style={st.okTitulo}>{titulo}</div>
      {children}
    </div>
  )
}

// NO es un <label>: un label reenvía el click al primer control que envuelve, y
// acá adentro hay selectores compuestos (input + 🔍 + modal + chip con ×). Con
// <label>, elegir un proveedor en la lista (F6) terminaba clickeando la × del
// chip recién pintado y se borraba solo (Martin, 15 sep 2026 — mismo bug que el
// recibo 900 del 25 ago, que había quedado arreglado solo en develop).
export function Campo({ label, children, style = {} }) {
  return (
    <div style={{ ...st.campo, ...style }}>
      <span style={st.label}>{label}</span>
      {children}
    </div>
  )
}

export function Badge({ children, tono = 'neutro' }) {
  const t = {
    neutro: { bg: colors.bgSoft, color: colors.textSec },
    ok: { bg: colors.okBg, color: colors.ok },
    warn: { bg: colors.warnBg, color: colors.warn },
    err: { bg: colors.errBg, color: colors.err },
    info: { bg: colors.infoBg, color: colors.info },
  }[tono]
  return <span style={{ ...st.badge, background: t.bg, color: t.color }}>{children}</span>
}

